import React from 'react';
import { cn } from '../../utils/cn';
import { User } from 'lucide-react';

const avatarSizes = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
};

const Avatar = React.forwardRef(({
  className,
  src,
  alt,
  fallback,
  size = 'md',
  ...props
}, ref) => {
  const sizeClasses = avatarSizes[size];
  const baseClasses = 'relative flex shrink-0 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800';
  
  const classes = cn(
    baseClasses,
    sizeClasses,
    className
  );

  const [imageError, setImageError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const showFallback = !src || imageError || !imageLoaded;

  return (
    <div
      ref={ref}
      className={classes}
      {...props}
    >
      {src && !imageError && (
        <img
          src={src}
          alt={alt}
          className="aspect-square h-full w-full object-cover"
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
      )}
      {showFallback && (
        <div className="flex h-full w-full items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
          {fallback ? (
            <span className="font-medium">{fallback}</span>
          ) : (
            <User className="h-1/2 w-1/2" />
          )}
        </div>
      )}
    </div>
  );
});

Avatar.displayName = 'Avatar';

const AvatarGroup = React.forwardRef(({
  className,
  children,
  max = 3,
  ...props
}, ref) => {
  const childrenArray = React.Children.toArray(children);
  const visibleChildren = childrenArray.slice(0, max);
  const remainingCount = childrenArray.length - max;

  return (
    <div
      ref={ref}
      className={cn('flex -space-x-2', className)}
      {...props}
    >
      {visibleChildren.map((child, index) => (
        <div key={index} className="ring-2 ring-white dark:ring-gray-800 rounded-full">
          {child}
        </div>
      ))}
      {remainingCount > 0 && (
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-xs font-medium text-gray-600 dark:text-gray-400 ring-2 ring-white dark:ring-gray-800">
          +{remainingCount}
        </div>
      )}
    </div>
  );
});

AvatarGroup.displayName = 'AvatarGroup';

export { Avatar, AvatarGroup, avatarSizes };
